'use client'

import { useTenantButtonColor } from '@/components/TenantButtonColorContext'
import { useTenantDarkMode } from '@/components/TenantThemeModeContext'
import { brandTextColorStyle } from '@/lib/colorUtils'
import { ScenarioNodeWithVideoUrl } from '@/orval/generated/public/model'
import { FC } from 'react'
import PerformerCard from './PerformerCard'
import VideoTextSection from './VideoTextSection'

export interface VideoInfoContentProps {
  currentNode: ScenarioNodeWithVideoUrl
}

/**
 * 動画情報の本文（タイトル / 出演者 / 動画テキスト）。
 *
 * TILE のパネル（VideoInfoPanel）と BOX の右パネルで同じ表示にするため、レイアウトから
 * 切り出した共通部品。出演者は2人ぶん、動画テキストは2枠ぶんを表示し、プロフィールと
 * 本文は Markdown で描画する（テンプレート優先は元の VideoInfoPanel と同じ規則）。
 *
 * 見た目は .info-* / .performer-* に依存するため、呼び出し側で VideoInfoPanelStyles を
 * 描画すること。配置・スクロール・下端スタックも呼び出し側が持つ。
 */
const VideoInfoContent: FC<VideoInfoContentProps> = ({ currentNode }) => {
  const titleStyle = brandTextColorStyle(useTenantButtonColor(), useTenantDarkMode())
  // Get video info from API data (template takes priority, show each performer separately)
  const title = currentNode.video?.title || currentNode.title || '動画タイトル'
  const performer1Name = currentNode.video?.performerTemplate?.name || currentNode.video?.performer || null
  const performer1Profile = currentNode.video?.performerTemplate?.profile || null
  const performer2Name = currentNode.video?.performerTemplate2?.name || null
  const performer1Department = currentNode.video?.performerTemplate?.department || null
  const performer2Profile = currentNode.video?.performerTemplate2?.profile || null
  const performer2Department = currentNode.video?.performerTemplate2?.department || null
  const videoText1 = currentNode.video?.videoTextTemplate?.videoText || currentNode.video?.videoText || ''
  const videoText2 = currentNode.video?.videoTextTemplate2?.videoText || ''

  return (
    <>
      <h2 className="info-title" style={titleStyle}>
        {title}
      </h2>

      {(performer1Name || performer2Name) && (
        <div className="info-section">
          {/* 「出演者」ラベルは置かない。アバター＋氏名＋部署のカードで自明なうえ、
              セクション見出しはクローラー向けの意味付けとして効くもので、Shadow DOM の
              埋め込みモーダルでは効かない。SEO が要る動画個別ページ側にだけ残す。 */}
          <div className="performer-members-group">
            {performer1Name && (
              <PerformerCard name={performer1Name} profile={performer1Profile} department={performer1Department} />
            )}
            {performer2Name && (
              <PerformerCard name={performer2Name} profile={performer2Profile} department={performer2Department} />
            )}
          </div>
        </div>
      )}
      {videoText1 && <VideoTextSection content={videoText1} />}
      {videoText2 && <VideoTextSection content={videoText2} />}
    </>
  )
}

export default VideoInfoContent
